import { useEffect, useRef } from 'react';
import { useContent } from '@/contexts/ContentContext';
import { supabase } from '@/integrations/supabase/client';
import { useEditMode } from '@/contexts/EditModeContext';
import { InlineContentEditor } from '@/components/InlineContentEditor';
import { createElement } from 'react';
import DOMPurify from 'dompurify';

/**
 * Hook to make it easy to add new editable content to any component
 * 
 * Usage:
 * const editableText = useEditableContent('my.new.key', 'Default Text', 'category', 'Description for content manager');
 * 
 * This will:
 * 1. Return the current content from the database
 * 2. Automatically create the content entry if it doesn't exist
 * 3. Make it editable in the Content Manager
 */
export function useEditableContent(
  key: string, 
  defaultContent: string, 
  category: string = 'general',
  description?: string
): string {
  const { getContent } = useContent();
  const hasInitialized = useRef(false);
  
  // Ensure the content exists in database - only run once
  useEffect(() => {
    if (hasInitialized.current) return;
    hasInitialized.current = true;
    
    const ensureContentExists = async () => {
      try {
        // Check if content already exists
        const { data: existing } = await supabase
          .from('app_content')
          .select('id')
          .eq('key', key)
          .maybeSingle();
        
        if (!existing) {
          // Use upsert to handle race conditions gracefully
          const { error } = await supabase
            .from('app_content')
            .upsert({
              key,
              content: defaultContent,
              category,
              description
            }, {
              onConflict: 'key',
              ignoreDuplicates: true
            });
          
          if (error && error.code !== '23505') {
            // Ignore duplicate key errors (23505), others handled silently
          }
        }
      } catch {
        // Error ensuring content exists - handled silently
      }
    };
    
    ensureContentExists();
  }, [key, defaultContent, category, description]);
  
  // Always return plain content - wrapping is done by EditableText component
  return getContent(key, defaultContent);
}

/**
 * Component version that wraps content in inline editor when edit mode is active
 * Use this when you need inline editing functionality
 */
export function EditableText({ 
  contentKey, 
  defaultContent, 
  category = 'general',
  description,
  className
}: { 
  contentKey: string;
  defaultContent: string;
  category?: string;
  description?: string;
  className?: string;
}) {
  const content = useEditableContent(contentKey, defaultContent, category, description);
  const { isEditMode } = useEditMode();
  
  if (isEditMode) {
    return createElement(InlineContentEditor, {
      contentKey,
      category,
      children: content
    });
  }
  
  // Check if content contains HTML tags
  const hasHTML = /<[^>]*>/g.test(content);
  
  // Strip inline color styles to use semantic tokens and sanitize
  const cleanContent = DOMPurify.sanitize(
    content
      .replace(/style="[^"]*color[^"]*"/gi, '')
      .replace(/color:\s*[^;]+;?/gi, '')
  );
  
  if (hasHTML) {
    return createElement('span', { 
      className: className || 'text-foreground',
      dangerouslySetInnerHTML: { __html: cleanContent }
    });
  }
  
  return createElement('span', { className: className || 'text-foreground' }, cleanContent);
}

/**
 * Batch create multiple content entries at once
 * Useful when adding a new feature with multiple text elements
 */
export async function createContentBatch(entries: Array<{
  key: string;
  content: string;
  category: string;
  description?: string;
}>) {
  try {
    // Insert all entries, ignore conflicts (in case some already exist)
    const { error } = await supabase
      .from('app_content')
      .upsert(entries, { onConflict: 'key', ignoreDuplicates: true });
    
    if (error) throw error;
    
    // Created content entries successfully
    return true;
  } catch {
    // Error creating content batch - handled silently
    return false;
  }
}